import { useTranslation } from 'react-i18next';
import { MonthLabels, WeekdayLabels, WeekdayOptions, daysInMonth } from '../../../utils/scheduleRuleConstants';

/**
 * Building blocks shared by the task schedule rule editor and the billing rule of offer
 * positions (FAFD-27), so both pick weekdays and dates the same way.
 */

interface WeekdayPickerProps {
  value: number[];
  onToggle: (day: number) => void;
  /** Two-letter labels for narrow layouts. */
  compact?: boolean;
}

export function WeekdayPicker({ value, onToggle, compact = false }: WeekdayPickerProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {WeekdayOptions.map((day) => {
        const isSelected = value.includes(day);
        return (
          <button
            key={day}
            type="button"
            onClick={() => onToggle(day)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              isSelected
                ? 'bg-blue-500 text-white dark:bg-blue-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
            title={WeekdayLabels[day]}
            aria-pressed={isSelected}
          >
            {compact ? WeekdayLabels[day].substring(0, 2) : WeekdayLabels[day]}
          </button>
        );
      })}
    </div>
  );
}

interface DayMonthSelectProps {
  label: string;
  month: number;
  day: number;
  onChange: (month: number, day: number) => void;
  labelClass: string;
  selectClass: string;
}

/** Day + month of a yearly-recurring date; the day is clamped when the month gets shorter. */
export function DayMonthSelect({ label, month, day, onChange, labelClass, selectClass }: DayMonthSelectProps) {
  const { t } = useTranslation(['tasks']);

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="grid grid-cols-2 gap-2">
        <select
          value={day}
          onChange={(e) => onChange(month, parseInt(e.target.value))}
          className={selectClass}
          aria-label={t('tasks:scheduleRuleForm.day')}
        >
          {Array.from({ length: daysInMonth(month) }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <select
          value={month}
          onChange={(e) => {
            const next = parseInt(e.target.value);
            onChange(next, Math.min(day, daysInMonth(next)));
          }}
          className={selectClass}
          aria-label={t('tasks:scheduleRuleForm.month')}
        >
          {Object.entries(MonthLabels).map(([val, monthLabel]) => (
            <option key={val} value={val}>{monthLabel}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

interface MonthSelectProps {
  label: string;
  value: number;
  onChange: (month: number) => void;
  labelClass: string;
  selectClass: string;
}

const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export function MonthSelect({ label, value, onChange, labelClass, selectClass }: MonthSelectProps) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <select value={value} onChange={(e) => onChange(parseInt(e.target.value))} className={selectClass}>
        {ALL_MONTHS.map((m) => (
          <option key={m} value={m}>{MonthLabels[m]}</option>
        ))}
      </select>
    </div>
  );
}
